import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Select } from './Select';
import { MultiSelectOption } from './MultiSelectOption';
import { SingleSelectOption } from './SingleSelectOption';
import { useOnClickOutside }  from '../../utilities/useOnClickOutside/useOnClickOutside';

export type Option = {
  value: string;
  display: string;
};

interface CommonProps {
  id: string;
  options: Option[];
  placeholder: string;
  name?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  labelDisplay: string;
  containerClass?: string;
  onChange: (item: any) => void;
}

type SingleOrMulti =
  | {
      multiSelect?: false;
      defaultValue?: Option;
    }
  | {
      multiSelect: true;
      defaultValue?: Option[];
    };

export type SelectInputProps = CommonProps & SingleOrMulti;

/**
 * The `SelectInput` mimics how a `select` works with additional capabilities such as selecting multiple items from the dropdown.
 * It also utilizes custom styling that cannot be achieved with a normal `select` HTML element but it functions are very similar. 
 * Pass in an array of objects with the two properties `value` and `display` in the `options` attribute. `onChange` returns the value(s) selected. 
  
 * * `options` should take in an array of objects with two properties:
  ```
  [{ value: string, display: string }]
  ```

  * * `value` is the actual data that is selected from the dropdown.
 
  * * `display` is what gets shown in the dropdown.
 
 *

 * * `multiSelect` allows the component to utilize multiple selections.

 *

 * * The `labelDisplay` prop should create a label for the input element.

 *

 * * The `helperText` prop should create text below the input element for additional information.

 *
 * * The `placeholder` prop adds a placeholder in the dropdown menu. Note that placeholder will be overridden by the selected option if `multiSelect` is disabled.

 *

 * * The `containerClass` prop should apply classNames to the entire component - added to controls only due to width not adjusting when placeholder content/select item width changes

 * 

 * * The `defaultValue` allows value(s) in the options to be pre-selected. 
 
    ** If you are not using `multiSelect`, pass in one object:
    ```
    { value: string, display: string }
    ```
    
    ** If using `multiSelect`, pass in an array of objects:
    ```
    [{ value: string, display: string }]
    ```
 */

export const SelectInput = ({
  id,
  options,
  defaultValue,
  placeholder,
  name,
  helperText,
  labelDisplay,
  disabled,
  required,
  containerClass,
  multiSelect,
  onChange,
}: SelectInputProps) => {
  const disabledClass = disabled && 'disabled';
  const requiredClass = required && !disabled && 'required';
  const containerClasses = containerClass || '';

  const lengthReference = useRef(null);
  const customSelect = useRef(null);
  const selectElement = useRef<HTMLSelectElement>(null);

  const [showingList, setShowingList] = useState<boolean>(false);
  const [inputFocused, setInputFocused] = useState<boolean>(false);

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [placeholderValue, setPlaceholderValue] = useState<string>('');

  const [widthOnSelect, setWidthOnSelect] = useState<number>(0);

  const findItem = (value) => options.find((option) => option.value === value);

  const getLongestDisplay = () =>
    [...options.map((option) => option.display), placeholder].sort((a, b) => b.length - a.length)[0];

    useOnClickOutside(customSelect, () => {
    setShowingList(false);
    setInputFocused(false);
  });

  const handleSingleChange = (value: string) => {
    if (value === '') {
      setSelectedOptions([{ value: '', display: placeholder }]);
    } else {
      const item = findItem(value);

      setSelectedOptions([item]);
      setShowingList(false);
    }
  };

  const handleMultipleChange = (event) => {
    if (event.target.tagName === 'SELECT') {
      var options = event.target.options;
      var items = [];

      for (var index = 0; index < options.length; index++) {
        if (options[index].selected && options[index].value !== '') {
          items.push(findItem(options[index].value));
        }
      }

      setSelectedOptions(items);
    } else {
      const item = findItem(event.target.value);

      if (event.target.checked) {
        selectElement.current.selectedIndex = 0;
        setSelectedOptions([...selectedOptions, item]);
      } else {
        selectedOptions.length > 0 ? selectElement.current.selectedIndex = 0 : selectElement.current.selectedIndex = -1;
        setSelectedOptions(selectedOptions.filter((option) => option.value !== item.value));
      }
    }
  };

  const setDefaultValueOnLoad = () => {
    if (defaultValue) {
      if (Array.isArray(defaultValue)) {
        setSelectedOptions(defaultValue);
      } else {
        setSelectedOptions([defaultValue]);
      }
    }

    setPlaceholderValue(placeholder);
  };

  const handleSelectionChange = () => {
    // updates placeholder with display if it's not multiSelect
    if (!multiSelect && selectedOptions.length > 0)
      setPlaceholderValue(selectedOptions[0].display);

    // returns selectedOptions onChange
    if (onChange) onChange(selectedOptions);
  };

  // This code ensures that when multiSelect is true, it takes an array of values, and if false, it takes a value.
  // It also checks if no defaultValue gets passed, to utilize undefined on load.
  const setValueOnSelectElement = () => {
    if (multiSelect) {
      return selectedOptions.map((option) => option.value);
    }

    if (selectedOptions.length === 0) {
      return undefined;
    }

    return selectedOptions[0].value;
  };

  // sets default value to the select when it loads. It also sets the placeholder to the state
  useEffect(setDefaultValueOnLoad, []);

  useEffect(handleSelectionChange, [selectedOptions]);

  useEffect(() => setPlaceholderValue(placeholder || 'Select...'), [placeholder]);

  // sets the max width when the lengthReference changes
  useEffect(() => setWidthOnSelect(lengthReference.current.offsetWidth), [lengthReference]);

  return (
    <SelectInputContainer
      className={`AD_selectInput position-relative ${containerClasses}`}
      style={{ width: `${widthOnSelect + 90}px` }}
    >
      {labelDisplay && (
        <label
          htmlFor={id}
          className={`AD_textLabel ${disabledClass} ${requiredClass}`}
        >
          {labelDisplay}
        </label>
      )}

      <Select
        name={name}
        id={id}
        disabled={disabled}
        required={required}
        multiple={multiSelect}
        value={setValueOnSelectElement()}
        onChange={(event) => {
          if (!multiSelect) {
            handleSingleChange(event.target.value);
          } else {
            handleMultipleChange(event);
          }
        }}
        onFocus={() => setInputFocused(true)}
        onBlur={() => setInputFocused(false)}
        options={options}
        selectRef={selectElement}
      />

      {/* This div is specifically for clicking outside */}
      <div ref={customSelect} className="position-relative">
        <div
          className={`select-selected d-flex flex-row align-items-center ${showingList && 'select-arrow-active'} ${inputFocused && 'focused'} ${disabledClass}`}
          onClick={() => {
            setShowingList(!showingList);
            setInputFocused(true);
          }}
        >
          {/* this span is used to calculate how long the selectInput needs to be */}
          <span
            ref={lengthReference}
            className="length-reference position-absolute"
          >
            {getLongestDisplay()}
          </span>
          <span
            className={`badge badge-pill ${!multiSelect || selectedOptions.length === 0 ? 'hidden mr-0' : 'mr-2'}`}
          >
            {selectedOptions.length}
          </span>
          <span>{placeholderValue}</span>
        </div>

        {showingList && (
          <div className="select-items position-absolute">
            {options.map((item, index) => {
              if (multiSelect) {
                return (
                  <MultiSelectOption
                    key={index}
                    index={index}
                    item={item}
                    options={selectedOptions}
                    onChange={(event) => handleMultipleChange(event)}
                  />
                );
              } else {
                return (
                  <SingleSelectOption
                    key={index}
                    item={item}
                    onClick={handleSingleChange}
                  />
                );
              }
            })}
          </div>
        )}
      </div>

      {helperText && (
        <span className={`AD_subtext ${disabledClass}`}>{helperText}</span>
      )}
    </SelectInputContainer>
  );
};

export default SelectInput;

export const SelectInputContainer = styled.div`
  min-width: 10em;

  .length-reference {
    opacity: 0;
    left: 0;
    z-index: -1;
  }

  label,
  span {
    &.disabled {
      color: #9e9e9e;
      color: var(--color-neutral-500);
      cursor: default;
      pointer-events: none;
    }
  }

  select {
    opacity: 0;
    width: 1px;
    height: 1px;
    bottom: 0;
  }

  .required:after {
    content: ' *';
    color: #db0020;
    color: var(--color-danger-500);
  }

  .select-selected {
    background-color: #ededed;
    background-color: var(--color-neutral-100);

    &.disabled {
      background-color: #ededed;
      background-color: var(--color-neutral-100);
      color: #9e9e9e;
      color: var(--color-neutral-500);
      cursor: default;
      pointer-events: none;
    }

    &.focused {
      border-bottom: 1px solid #0082ad;
      border-bottom: 1px solid var(--color-primary-600);
    }
  }

  .select-selected:after {
    border-style: solid;
    border-width: 1px 1px 0 0;
    content: '';
    position: absolute;
    transform: rotate(135deg);
    height: 0.45em;
    width: 0.45em;
    right: 1em;
  }

  .select-arrow-active:after {
    transform: rotate(-45deg);
  }

  .select-items > div,
  .select-selected {
    color: rgba(0, 0, 0, 0.8);
    color: var(--color-neutral-1000-alpha-8);
    padding: 0.5em 1em;
    border: 1px solid transparent;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid var(--color-neutral-1000-alpha-1);
    cursor: pointer;
    user-select: none;

    .disabled & {
      color: #9e9e9e;
      color: var(--color-neutral-500);
    }
    .AD_checkbox .checkBox {
      margin-right: 0.5rem;
    }
  }

  .select-items {
    background-color: #ededed;
    background-color: var(--color-neutral-100);
    top: 100%;
    left: 0;
    right: 0;
    z-index: 99;
  }

  .select-items > div:hover,
  .same-as-selected {
    background-color: rgba(0, 0, 0, 0.1);
    background-color: var(--color-neutral-1000-alpha-1);
  }

  .badge {
    color: #fff;
    color: var(--color-neutral-0);
    background-color: #757575;
    background-color: var(--color-neutral-700);
    transition: all 0.15s ease-out;
  }

  .hidden {
    font-size: 0;
    width: 0px;
  }
  .AD_textLabel {
    font-size: 0.75rem;
    line-height: 1.5rem;
    letter-spacing: 0.005rem;

    &.disabled {
      color: #9e9e9e;
      color: var(--color-neutral-500);
    }

    &.required {
      &:after {
        content: '*';
        margin-left: 0.15rem;
        display: inline-block;
        color: #db0020;
        color: var(--color-danger-500);
      }
    }
  }
  .AD_subtext {
    font-size: 0.75rem;
    color: #616161;
    color: var(--color-neutral-700);
    line-height: 1.5rem;
    letter-spacing: 0.005rem;

    &.disabled {
      color: #9e9e9e;
      color: var(--color-neutral-500);
    }
  }
`;
